import React from "react";
import { submitMedikreditClaim } from "../../mediKreditapi";
import {
  isLineDateOnly,
  CLAIM_DEFAULT_START_HHMMSS,
  CLAIM_DEFAULT_END_HHMMSS,
} from "../../utils/appointmentTimes";
import { buildClaimDiscounts, round2 } from "../../utils/claimDiscounts";

const pad = (n) => String(n).padStart(2, "0");

function formatDateYYYYMMDD(dateStr) {
  if (!dateStr) return "";
  return String(dateStr).slice(0, 10).replace(/-/g, "");
}

// Local time → "HHMMSS" (no UTC conversion)
function toHHMMSSLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${hh}${mm}${ss}`;
}

function extractDatesAndTimes(line, fallbackInvoiceDate) {
  const startISO = line.appointmentCustomStart || line.appointmentStartSet || null;
  const endISO   = line.appointmentCustomEnd   || line.appointmentEndSet   || null;
  const dateOnly = line.appointmentDateCustom || line.appointmentDateSet || fallbackInvoiceDate || "";

  // Date-only lines (both start and end at midnight): the medical-aid switch
  // still wants a time component, so send a default 08:00–09:00 window.
  const dateOnlyLine = isLineDateOnly(line);

  let stDate = "", endDate = "", stTime = "", endTime = "";

  if (startISO || endISO) {
    const s = startISO ? new Date(startISO) : null;
    const e = endISO ? new Date(endISO) : null;

    stDate  = s ? startISO.slice(0, 10) : (dateOnly || "");
    endDate = e ? endISO.slice(0, 10)   : (stDate || dateOnly || "");

    if (dateOnlyLine) {
      stTime = CLAIM_DEFAULT_START_HHMMSS;
      endTime = CLAIM_DEFAULT_END_HHMMSS;
    } else {
      stTime  = s ? toHHMMSSLocal(startISO) : "000000";
      endTime = e ? toHHMMSSLocal(endISO)   : stTime || "000000";
    }
  } else if (dateOnly) {
    stDate = endDate = dateOnly.slice(0, 10);
    stTime = "000000";
    endTime = "000000";
  }

  return {
    st_dt: formatDateYYYYMMDD(stDate),
    st_tm: stTime,
    end_dt: formatDateYYYYMMDD(endDate),
    end_tm: endTime,
  };
}

function parseCodeListMaybeSemicolon(strOrArr) {
  if (Array.isArray(strOrArr)) return strOrArr.filter(Boolean);
  if (!strOrArr) return [];
  return String(strOrArr)
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
}

function getCd1Array(line) {
  // Prefer explicit arrays if present
  if (Array.isArray(line.icd10Primary)) return line.icd10Primary.filter(Boolean);
  if (Array.isArray(line.icd10Primaries)) return line.icd10Primaries.filter(Boolean);
  if (Array.isArray(line.icd101)) return line.icd101.filter(Boolean);

  // Back-compat: single string or semi-colon list from icd101
  if (typeof line.icd101 === "string") {
    return parseCodeListMaybeSemicolon(line.icd101);
  }
  return [];
}

function getCd2Array(line) {
  if (Array.isArray(line.icd10Secondary)) return line.icd10Secondary.filter(Boolean);
  if (Array.isArray(line.icd102)) return line.icd102.filter(Boolean);
  if (line.icd102) return parseCodeListMaybeSemicolon(line.icd102);
  return [];
}

export async function SubmitClaim({
  practice,
  account,
  selectedPatient,
  invoice,
  claimtxn,
}) {
  // Fallback: if the fully-loaded practice doc hasn't populated yet (race against
  // a fast click), use the practiceNumber embedded on the invoice itself.
  const practiceNumber =
    practice?.practiceNumber || invoice?.practice?.practiceNumber;

  if (!practiceNumber) {
    throw new Error(
      "Practice details are still loading. Please wait a moment and try again."
    );
  }

  const practiceInfo = {
    sp_bhf: practiceNumber,
    sp_hpc: invoice.practitioner.hpcsa,
    grp_prac: practiceNumber,
    tx_nbr: claimtxn,
    // 14 returns the scheme's own codes and 250-char descriptions in the
    // extended tags; 13 capped every reason at 40 characters.
    msg_fmt: "14",
    tx_cd: "21",
  };

  const iodRef = String(selectedPatient?.patientIODClaimRefNumber ?? "")
  .replace(/[^a-zA-Z0-9/]/g, "") // keep only alphanumeric + /
  .split("/")
  .slice(0, 3) // keep only first 3 parts = 2 slashes
  .join("/");

  const medicalAidInfo = {
    plan: selectedPatient.patientMediKreditPlanNumber04,
    ch_id: selectedPatient.patientMedicalAidNumber || iodRef,
    sname: selectedPatient.patientLastName,
    inits: selectedPatient.patientInitials,
  };

  const IODInfo = {
    is_IOD: account.accountIODFund ? "Y" : "N",
    wca_nbr: account.accountIODEmployerWCARegNum,
    ins_ref_nbr: account.accountIODClaimRefNumber,
    dt_of_acdnt: formatDateYYYYMMDD(account.accountIODDateOfInjury),
    pre_iss_tx: "", // Not provided in current data model
  };
console.log("IOD Info:", IODInfo);  


  // ===== Discounts =====
  // Never claim items: see utils/claimDiscounts. A reversal replays this
  // exact request from storage, so it inherits the same discount shape.
  const invoiceLines = Array.isArray(invoice.lines) ? invoice.lines : [];
  const claimLines = invoiceLines.filter((l) => l?.type !== "discount");
  const medAidOf = (l) => Number(l?.medAidAmount ?? l?.originalRate ?? 0);

  const { subDiscForLine, totalDiscount } = buildClaimDiscounts(
    invoiceLines,
    claimLines,
    medAidOf,
  );

  // Item prices are sent gross-before-discount, so the claim-level total is
  // gross too. `netMedAidAmount` is what the invoice says is owed. The backend
  // reconciles sum(price) - sum(sub_disc) against it, then renders each item's
  // XML `price` (and the claim-level one) net of its discount.
  const netMedAidAmount = round2(invoice.totalMedicalAidAmount);

  const financialInfo = {
    totalMedAidAmount: round2(netMedAidAmount + totalDiscount),
    netMedAidAmount,
  };

  const patientInfo = {
  dep_cd: selectedPatient.patientMedicalAidDependantCode,
  dob: formatDateYYYYMMDD(selectedPatient.patientDOB),
  id_nbr: selectedPatient.patientIdNumber,

  // 🔹 Always uppercase and safely handle null/undefined
  fname: (selectedPatient.patientFirstName || "").toUpperCase(),
  sname: (selectedPatient.patientLastName || "").toUpperCase(),

  gend: selectedPatient.patientGender,
  rlnship: selectedPatient.patientRelationship,
  status: invoice.outPatient ? "O" : "I",
  placeOfService: invoice.placeOfService,

  // 🔹 Initials also uppercase
  int: (selectedPatient.patientInitials || "").toUpperCase(),
  accountNumber: account.accountNumber || "",
};


  // --- NEW: Case info comes from invoice.case (not inPatientCase) ---
  const casePrimaries =
    Array.isArray(invoice.case?.icd101)
      ? invoice.case.icd101.filter(Boolean)
      : invoice.case?.icd101
      ? [invoice.case.icd101]
      : [];
  const caseSecondaries =
    Array.isArray(invoice.case?.icd102) ? invoice.case.icd102.filter(Boolean) : [];

  const caseInfo = {
    ref_bhf: invoice.case?.referringDoctorBHF || "",
    ref_hpcsa: invoice.case?.referringDoctorHPCSA || "",
    auth_nbr: invoice.case?.authorizationNumber || "",
    auth_by: invoice.case?.authorizedBy || "",
    cd1: casePrimaries,   // array of primaries (stg_01)
    cd2: caseSecondaries, // array of secondaries (stg_02)
  };

 // Build transaction lines
let baseLineNumber = 1;
const transactionInfo = [];
const fallbackInvoiceDate = invoice.invoiceDate || "";

for (let i = 0; i < claimLines.length; i++) {
  const line = claimLines[i];

  let lineDescription = String(line.description ?? "");
  let lineTariffCode = String(line.tariffCode ?? "");
  const lineQuantity = Number(line.quantity ?? 1);

  const cd1Array = getCd1Array(line);
  const cd2Array = getCd2Array(line);

  const { st_dt, st_tm, end_dt, end_tm } = extractDatesAndTimes(line, fallbackInvoiceDate);

  const lineAuthNumber = caseInfo.auth_nbr || "";

  let modifiers = [];
  if (line.modifier) {
    modifiers = Array.isArray(line.modifier)
      ? line.modifier.filter(Boolean)
      : [line.modifier];
  }

  const hasNegativeModifier = modifiers.some((m) =>
    typeof m === "object" ? Number(m.rateChange) < 0 : false
  );

  const basePrice = hasNegativeModifier
    ? Number(line.medAidAmount ?? 0)
    : Number(line.medAidAmount ?? line.originalRate ?? 0);

  // NAPPI line: claim under the linked tariff code when one was chosen in
  // the Add NAPPI modal, otherwise under the generic 0201 materials code.
  // The NAPPI code is sent exactly as stored: modern 7-digit product codes
  // make the full code 10 digits, and truncating it changes the product
  // (MediKredit WARN 215 / RMR 352).
  let nappiCode = null;
  let nappiQty = null;
  if (line.type === "nappi" || line.nappiCode || line.selectedNappi) {
    // Spec: material/NAPPI item descr must be the NAPPI product description,
    // not the tariff's. Modal-added NAPPIs store it under `name`.
    const nappiDesc =
      line.selectedNappi?.description || line.selectedNappi?.name;
    if (nappiDesc) {
      lineDescription = String(nappiDesc);
    }
    lineTariffCode = line.linkedTariffCode ? String(line.linkedTariffCode) : "0201";
    nappiCode = String(line.nappiCode || line.selectedNappi?.nappiCode || line.selectedNappi?.code || "");
    nappiQty = Number(line.quantity ?? 1).toFixed(2);
  }

  // MediKredit ITEM descr is 40 ASCII chars (spec 3.53 "40a"). Longer or
  // non-ASCII values (e.g. the em-dash in linked-NAPPI invoice descriptions)
  // can reject the whole document upfront with no item echoes. Normalise
  // dashes, strip the rest, and truncate.
  lineDescription = lineDescription
    .replace(/[–—]/g, "-")
    .replace(/[^\x20-\x7E]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 40);

  // lin_num stays a contiguous 1..N over the items actually sent: the switch
  // has never seen gaps and this is not the place to find out whether it minds.
  const baseLineId = String(baseLineNumber);

  // Discount on this item. Left off the line entirely when zero so
  // undiscounted claims are byte-identical to what we sent before.
  const subDisc = subDiscForLine(line);

  const baseLine = {
    lin_num: baseLineId,
    // Not part of the XML. PostedInvoice matches each response line to its
    // invoice line by lineNumber, and lin_num can no longer stand in for that:
    // discount lines are not sent, so the two sequences diverge the moment an
    // invoice carries one. The backend uses this to re-point the response.
    invoiceLineNumber: line.lineNumber ?? null,
    pay_adv: "P",
    tp: "2",
    cdg_set: "02",
    auth_nbr: lineAuthNumber,
    descr: lineDescription,
    process_tp: "01",
    tar_cd: lineTariffCode,
    qty: "1",
    units: lineQuantity,
    unit_tp: "06",
    ...(nappiCode ? { nappi_cd: nappiCode, nappi_qty: nappiQty } : {}),
    st_dt,
    st_tm,
    end_dt,
    end_tm,
    cd1: cd1Array,
    cd2: cd2Array,
    tp_ind: "01",
    bhf_nbr: practiceInfo.sp_bhf,
    hpc_nbr: invoice.practitioner.hpcsa,
    tar_ind: "03",
    price: basePrice,
    gross: basePrice,
    bs_of_cst: "3",
    ...(subDisc > 0 ? { sub_disc: subDisc } : {}),
    sp_hpc: invoice.practitioner.hpcsa,
    oti: line.oti || "A",
  };
  transactionInfo.push(baseLine);


  // --- Modifier lines (tp="3") for this base line ---
  for (let j = 0; j < modifiers.length; j++) {
    const modifier = modifiers[j];
    const rateChange =
      typeof modifier === "object" ? Number(modifier.rateChange ?? 0) : 0;
    const modCode =
      typeof modifier === "object" ? modifier.code : String(modifier ?? "");
    const isPositive = rateChange > 0;

    // suffix: 0 -> "a", 1 -> "b", 2 -> "c", ...
    const suffix = String.fromCharCode("a".charCodeAt(0) + j);
    const modifierLineId = `${baseLineId}${suffix}`; // "1a", "1b", "2a", ...

    transactionInfo.push({
      lin_num: modifierLineId,   // <-- "1a", "1b", "1c", ...
      pay_adv: "P",
      tp: "3",
      cdg_set: "02",
      auth_nbr: lineAuthNumber,
      descr: lineDescription,
      process_tp: "01",
      tar_cd: lineTariffCode,
      qty: "1",
      units: lineQuantity,
      unit_tp: "06",
      st_dt,
      st_tm,
      end_dt,
      end_tm,
      cd1: cd1Array, // same ICD coding as base
      cd2: cd2Array,
      tp_ind: "01",
      bhf_nbr: practiceInfo.sp_bhf,
      hpc_nbr: invoice.practitioner.hpcsa,
      tar_ind: "03",
      price: isPositive ? rateChange : 0,
      gross: isPositive ? rateChange : 0,
      bs_of_cst: "3",
      sp_hpc: invoice.practitioner.hpcsa,
      modtp: "3",
      mod: modCode,
      oti: line.oti || "A",
    });
  }

  // Only now move on to the next base line number
  baseLineNumber++;
}

console.log("Prepared claim data:", {
  practiceInfo,
  medicalAidInfo,
  patientInfo,
  transactionInfo,
  financialInfo,
  caseInfo,
  IODInfo,
});

  try {
    const response = await submitMedikreditClaim(
      practiceInfo,
      medicalAidInfo,
      patientInfo,
      transactionInfo,
      financialInfo,
      caseInfo,
      IODInfo,
      selectedPatient?.patientMediKreditPlanNumber04,
      invoice?._id,
    );

    console.log("Claim Submission Data:", {
      practiceInfo,
      medicalAidInfo,
      patientInfo,
      transactionInfo,
      financialInfo,
      caseInfo,
      IODInfo,
    });

    console.log("✅ Claim Response:", response);
    return response;
  } catch (error) {
    console.error("❌ Claim Submission Failed:", error);
    throw error;
  }
}
